// Centralized per-route SEO metadata for public sub-routes.
// Used by <SEO> component + prerender pipeline to keep titles/descriptions in one place.

export interface RouteMeta {
  title: string;        // <60 chars
  description: string;  // <160 chars
  h1?: string;
  keywords?: string;
}

export const SITE_ORIGIN = "https://behtari.pk";
export const SITE_NAME = "Behtari";
export const DEFAULT_OG_IMAGE = "/images/og-social.jpg";

export const PUBLIC_ROUTE_META: Record<string, RouteMeta> = {
  "/": {
    title: "Pakistan's Blue-Collar Job Marketplace",
    description: "Hire CNIC-verified workers or find blue-collar jobs across Pakistan. AI matching, fair wages, zero commission on Behtari.",
    h1: "Behtari",
    keywords: "blue collar jobs Pakistan, hire workers, behtari, jobs Karachi Lahore Islamabad",
  },
  "/about": {
    title: "About Behtari | Verified Hiring in Pakistan",
    description: "Behtari (بہتری) is Pakistan's blue-collar job marketplace — CNIC-verified workers, employers and agencies in one trusted platform.",
    h1: "About Behtari",
  },
  "/how-it-works": {
    title: "How Behtari Works | Hire & Get Hired in Pakistan",
    description: "Learn how Behtari connects workers, employers and agencies in Pakistan. Sign up, verify CNIC, post or apply, hire safely.",
    h1: "How Behtari Works",
  },
  "/faqs": {
    title: "Behtari FAQs | Hiring & Working in Pakistan",
    description: "Common questions about hiring blue-collar workers, applying for jobs, payments, safety and verification on Behtari.",
    h1: "Frequently Asked Questions",
  },
  "/contact": {
    title: "Contact Behtari | Pakistan Support",
    description: "Reach Behtari support for help with hiring, applying, payments, or safety. Pakistan-based team, +92 332 2300004.",
    h1: "Contact Behtari",
  },
  "/for-workers": {
    title: "For Workers — Find Jobs in Pakistan | Behtari",
    description: "Pakistan's safest job marketplace for drivers, maids, plumbers, electricians, helpers, AC technicians and more. Apply free on Behtari.",
    h1: "For Workers",
  },
  "/for-employers": {
    title: "For Employers — Hire Verified Workers | Behtari",
    description: "Hire CNIC-verified household and personal workers anywhere in Pakistan. Browse rated profiles or post a job free on Behtari.",
    h1: "For Employers",
  },
  "/for-businesses": {
    title: "For Businesses — Bulk Hiring in Pakistan | Behtari",
    description: "Post multiple jobs, manage teams, and hire verified workers at scale. Built for Pakistani businesses on Behtari.",
    h1: "For Businesses",
  },
  "/for-agencies": {
    title: "For Staffing Agencies in Pakistan | Behtari",
    description: "Manage your worker roster, respond to employer requests, and grow placements across Pakistan with Behtari for agencies.",
    h1: "For Staffing Agencies",
  },
  "/cities/karachi": {
    title: "Hire & Find Jobs in Karachi | Behtari",
    description: "Karachi's largest verified blue-collar job marketplace. Drivers, maids, electricians, plumbers and more — CNIC-verified on Behtari.",
    h1: "Behtari in Karachi",
  },
  "/cities/lahore": {
    title: "Hire & Find Jobs in Lahore | Behtari",
    description: "Lahore's verified blue-collar job marketplace. Hire drivers, maids, helpers, technicians or apply for jobs on Behtari.",
    h1: "Behtari in Lahore",
  },
  "/cities/islamabad": {
    title: "Hire & Find Jobs in Islamabad | Behtari",
    description: "Islamabad's verified blue-collar job marketplace. Drivers, maids, helpers, technicians — CNIC-verified on Behtari.",
    h1: "Behtari in Islamabad",
  },
  "/safety": {
    title: "Safety Tips & Guidelines | Behtari",
    description: "Stay safe on Behtari. Tips on protecting your CNIC, verifying employers, secure payments, and reporting abuse across Pakistan.",
    h1: "Safety on Behtari",
    keywords: "behtari safety, hiring safety Pakistan, CNIC protection, worker safety tips",
  },
  "/privacy-policy": {
    title: "Privacy Policy | Behtari (Fast Solutions)",
    description: "How Behtari collects, uses, stores and protects your CNIC, contact and payment data under Pakistan's PECA 2016 and global standards.",
    h1: "Privacy Policy",
    keywords: "behtari privacy, data protection Pakistan, PECA 2016, CNIC privacy",
  },
  "/terms-of-service": {
    title: "Terms of Service | Behtari (Fast Solutions)",
    description: "The legal terms governing your use of Behtari — accounts, subscriptions, conduct, payments, disputes and Pakistan-specific compliance.",
    h1: "Terms of Service",
    keywords: "behtari terms, terms of service Pakistan, user agreement",
  },
};

// Routes that must NEVER be indexed (auth/utility/transactional).
// Mirrors the previous MetaManager allow-list for backward parity.
export const NOINDEX_ROUTES: string[] = [
  "/login",
  "/signup",
  "/persona-selection",
  "/join-team",
  "/accept-invite",
  "/reset-password",
  "/reset-password-cnic",
  "/reset-password-by-cnic",
  "/reset",
  "/verify-email",
  "/payment-return",
  "/payment-instructions",
  "/account-deletion",
];

export const isNoIndexPath = (pathname: string): boolean =>
  NOINDEX_ROUTES.some((p) => pathname === p || pathname.startsWith(p + "/")) ||
  pathname.startsWith("/dashboard") ||
  pathname.startsWith("/profile/") ||
  pathname.startsWith("/handlers/");

export const PUBLIC_NAV_LINKS: { to: string; labelKey: string; fallback: string }[] = [
  { to: "/about", labelKey: "landing.nav.about", fallback: "About" },
  { to: "/how-it-works", labelKey: "landing.nav.howItWorks", fallback: "How it works" },
  { to: "/for-workers", labelKey: "landing.nav.forWorkers", fallback: "For workers" },
  { to: "/for-employers", labelKey: "landing.nav.forEmployers", fallback: "For employers" },
  { to: "/for-businesses", labelKey: "landing.nav.forBusinesses", fallback: "For businesses" },
  { to: "/for-agencies", labelKey: "landing.nav.forAgencies", fallback: "For agencies" },
  { to: "/faqs", labelKey: "landing.nav.faqs", fallback: "FAQs" },
  { to: "/contact", labelKey: "landing.nav.contact", fallback: "Contact" },
];

export const CITY_LIST = [
  { slug: "karachi", name: "Karachi" },
  { slug: "lahore", name: "Lahore" },
  { slug: "islamabad", name: "Islamabad" },
];
